import React, { useRef, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Chip,
  Divider,
  Popover,
  Dialog,
  Button,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DeleteIcon from "@material-ui/icons/Delete";
import AddPhotoAlternateIcon from "@material-ui/icons/AddPhotoAlternate";
import StarIcon from "@material-ui/icons/Star";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import RLDD from "react-list-drag-and-drop/lib/RLDD";
import { authDelete, authPatch, AuthRequest } from "../../../lib";
import { AuthSelectors } from "../../../state/auth/selectors";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import VisibilityIcon from "@material-ui/icons/Visibility";
import Tooltip from "@material-ui/core/Tooltip";

const IMAGES_BASE_URL = process.env.REACT_APP_BASEURL;

const FlatCheckbox = withStyles({
  root: { "&:hover": { backgroundColor: "transparent" } },
})(Checkbox);

const FEEDS = [
  { key: "prestige_time", label: "Prestige Time" },
  { key: "brand",         label: "Brand / Google" },
  { key: "watch_source",  label: "The Watch Source" },
  { key: "chrono24",      label: "Chrono24" },
];

export function cleanModelNo(modelNo) {
  return String(modelNo)
    .replace(/ /g, "_")
    .replace(/\//g, "~")
    .replace(/\./g, "-");
}

export function buildImageUrl(feed, itemId, filename, brandId, seriesId, modelNo) {
  if (!filename) return null;
  if (feed === "chrono24")     return `${IMAGES_BASE_URL}/images/nw-feed/${itemId}/${filename}`;
  if (feed === "watch_source") return `${IMAGES_BASE_URL}/images/tws/${filename}`;
  if (brandId != null && modelNo != null)
    return `${IMAGES_BASE_URL}/images/watches/${brandId}/${seriesId ?? 0}/${cleanModelNo(modelNo)}/${filename}`;
  return `${IMAGES_BASE_URL}/images/watches/${filename}`;
}

function formatBytes(bytes) {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(val) {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function FeedSection({ feedKey, label, photos, isMainFeed, onChange, onDelete, uploadNow, itemId, currentUser, brandId, seriesId, modelNo }) {
  const [dropzoneHover, setDropzoneHover] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverPhoto, setPopoverPhoto] = useState(null);
  const [popoverDims, setPopoverDims] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [deleteDialogPhoto, setDeleteDialogPhoto] = useState(null);
  const [deleteDialogIndex, setDeleteDialogIndex] = useState(null);
  const fileInputRef = useRef();
  const sectionRef = useRef();
  // Always-fresh refs so native listeners never close over stale state
  const addFilesRef = useRef();
  const photosRef = useRef(photos);
  photosRef.current = photos;

  const addFiles = async (files) => {
    const fileArray = Array.from(files);

    if (!uploadNow) {
      // Add-watch mode: queue files for upload on save
      const newPhotos = fileArray.map((file, i) => ({
        file,
        previewUrl: URL.createObjectURL(file),
        caption: null,
        own_pic: feedKey === "prestige_time" ? 1 : 0,
        // is_shareable: feedKey === "prestige_time" || feedKey === "brand" ? 1 : 0, // dormant — see "PT Display" below
        // Brand feed: only the very first/main image (no existing photos + first
        // of this batch) starts visible; the rest upload unchecked for the admin
        // to enable manually. Other feeds keep default-visible (prestige_time
        // relies on pt_display=1 to show at all).
        pt_display: feedKey === "brand" ? (photosRef.current.length === 0 && i === 0 ? 1 : 0) : 1,
        sort_order: photosRef.current.length + i,
      }));
      onChange([...photosRef.current, ...newPhotos]);
      return;
    }

    // Edit mode: show uploading placeholders immediately, then replace with saved records
    const tempBase = Date.now();
    const tempPhotos = fileArray.map((file, i) => ({
      _tempId: tempBase + i,
      file,
      previewUrl: URL.createObjectURL(file),
      caption: null,
      own_pic: feedKey === "prestige_time" ? 1 : 0,
      // is_shareable: feedKey === "prestige_time" || feedKey === "brand" ? 1 : 0, // dormant — see "PT Display" below
      // Brand feed: only the first/main image starts visible (see addFiles queue
      // path); the rest upload unchecked. Other feeds default-visible.
      pt_display: feedKey === "brand" ? (photosRef.current.length === 0 && i === 0 ? 1 : 0) : 1,
      sort_order: photosRef.current.length + i,
      uploading: true,
    }));
    onChange([...photosRef.current, ...tempPhotos]);

    try {
      const savedImages = await uploadNow(fileArray, tempPhotos.map((p) => p.pt_display));
      const saved = savedImages.map((img) => ({
        image_id:     img.image_id,
        filename:     img.filename,
        previewUrl:   buildImageUrl(feedKey, itemId, img.filename, brandId, seriesId, modelNo),
        caption:      img.caption,
        own_pic:      img.own_pic,
        is_shareable: img.is_shareable, // kept on the row so dormant column survives round-trip
        pt_display:   img.pt_display,
        sort_order:   img.sort_order,
      }));
      // Remove temp entries and append saved ones
      onChange([...photosRef.current.filter((p) => !p.uploading), ...saved]);
    } catch (err) {
      // Remove failed placeholders
      onChange(photosRef.current.filter((p) => !p.uploading));
      alert("Image upload failed: " + (err?.message ?? err));
    }
  };
  addFilesRef.current = addFiles;

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const onDragEnter = (e) => { e.preventDefault(); setDragOver(true); };
    const onDragOver  = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; };
    const onDragLeave = (e) => { if (!el.contains(e.relatedTarget)) setDragOver(false); };
    const onDrop = (e) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        addFilesRef.current(e.dataTransfer.files);
      }
    };

    el.addEventListener("dragenter", onDragEnter, { capture: true, passive: false });
    el.addEventListener("dragover",  onDragOver,  { capture: true, passive: false });
    el.addEventListener("dragleave", onDragLeave, { capture: false });
    el.addEventListener("drop",      onDrop,      { capture: true, passive: false });

    return () => {
      el.removeEventListener("dragenter", onDragEnter, { capture: true });
      el.removeEventListener("dragover",  onDragOver,  { capture: true });
      el.removeEventListener("dragleave", onDragLeave, { capture: false });
      el.removeEventListener("drop",      onDrop,      { capture: true });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = "";
    }
  };

  const handleReorder = (reordered) => {
    const withOrder = reordered.map((p, i) => ({ ...p, sort_order: i }));
    onChange(withOrder);
    // Persist the new order immediately — a drag-drop should save on drop, not only
    // when the whole watch is "Updated". Fire the PATCHes SEQUENTIALLY so the backend's
    // pic_big recalc (which promotes the new first prestige_time/brand image to the
    // main image) settles on the final order — the last PATCH runs after every
    // sort_order write. RLDD only renders when all images are saved, so image_id
    // is always present here.
    if (itemId) {
      (async () => {
        for (let i = 0; i < withOrder.length; i++) {
          const p = withOrder[i];
          if (p.image_id != null && reordered[i].sort_order !== i) {
            try { await authPatch(`/images/${p.image_id}`, { sort_order: i }); } catch (e) { /* ignore */ }
          }
        }
      })();
    }
  };

  const handleCaptionChange = (index, value) => {
    onChange(photos.map((p, i) => i === index ? { ...p, caption: value } : p));
  };

  // Dormant — Shareable checkbox is commented out below. Preserve the handler
  // so we can re-enable both quickly without re-deriving the wiring.
  // const handleShareableChange = (index, checked) => {
  //   onChange(photos.map((p, i) => i === index ? { ...p, is_shareable: checked ? 1 : 0 } : p));
  // };
  const handlePtDisplayChange = (index, checked) => {
    onChange(photos.map((p, i) => i === index ? { ...p, pt_display: checked ? 1 : 0 } : p));
  };

  const allSaved = photos.every((p) => p.image_id != null);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [grabbingIdx, setGrabbingIdx] = useState(null);

  useEffect(() => {
    const onMouseUp = () => setGrabbingIdx(null);
    window.addEventListener("mouseup", onMouseUp);
    return () => window.removeEventListener("mouseup", onMouseUp);
  }, []);

  const isPicBigSlot = isMainFeed && photos.length > 0;

  const handleInfoClick = (e, item) => {
    e.stopPropagation();
    const photo = item;
    setPopoverAnchor(e.currentTarget);
    setPopoverPhoto(photo);
    setPopoverDims(null);
    if (!photo.width && photo.previewUrl) {
      const img = new Image();
      img.onload = () => setPopoverDims({ width: img.naturalWidth, height: img.naturalHeight });
      img.src = photo.previewUrl;
    }
  };

  const renderItem = (item, index) => (
    <Box
      key={item.image_id ?? item._tempId ?? index}
      onMouseEnter={() => setHoveredIdx(index)}
      onMouseLeave={() => setHoveredIdx(null)}
      onMouseDown={() => allSaved && setGrabbingIdx(index)}
      style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 120 }}
    >
      <Box style={{ position: "relative" }}>
        {isMainFeed && index === 0 && (
          <StarIcon style={{ position: "absolute", top: 2, left: 2, fontSize: 16, color: "#1976d2", zIndex: 1 }} />
        )}
        <img
          src={item.previewUrl}
          alt=""
          draggable={false}
          style={{
            width: 110, height: 170, objectFit: "contain", backgroundColor: "#ffffff", padding: "4px 0",
            border: "1px solid rgba(0,0,0,0.15)", borderRadius: 4, display: "block",
            opacity: item.uploading ? 0.4 : 1,
            cursor: !allSaved ? "default" : grabbingIdx === index ? "grabbing" : "grab",
            boxShadow: allSaved && hoveredIdx === index ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
            transform: allSaved && hoveredIdx === index ? "scale(1.02)" : "scale(1)",
            transition: "box-shadow 0.15s, transform 0.15s",
          }}
        />
        {item.uploading && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, color: "#1976d2", fontWeight: 600,
          }}>
            Uploading…
          </div>
        )}
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setDeleteDialogPhoto(item); setDeleteDialogIndex(index); }}
          style={{ position: "absolute", top: 2, right: 2, backgroundColor: "rgba(255,255,255,0.85)", padding: 2 }}
        >
          <DeleteIcon style={{ fontSize: 16, color: "#d32f2f" }} />
        </IconButton>
        {!item.uploading && (
          <IconButton
            size="small"
            onClick={(e) => handleInfoClick(e, item)}
            style={{ position: "absolute", bottom: 2, right: 2, backgroundColor: "rgba(255,255,255,0.85)", padding: 2 }}
          >
            <InfoOutlinedIcon style={{ fontSize: 14, color: "#555" }} />
          </IconButton>
        )}
        <Tooltip title="View image">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (item.previewUrl) {
              setPreviewImage(item.previewUrl);
              setPreviewOpen(true);
            }
          }}
          style={{
            position: "absolute",
            bottom: 2,
            left: 2,
            backgroundColor: "rgba(255,255,255,0.85)",
            padding: 2,
          }}
        >
          <VisibilityIcon style={{ fontSize: 14, color: "#555" }} />
          {/** <OpenInNewIcon style={{ fontSize: 14, color: "#555" }} /> */}
        </IconButton>
        </Tooltip>
      </Box>

      {/* Dormant — original "Shareable" checkbox (cross-feed sharing for Brand/Google).
          Preserved here in commented form so it can be re-enabled quickly. The DB
          column `is_shareable` is still maintained on the row through round-trips.
      {(feedKey === "brand" || feedKey === "watch_source" || feedKey === "chrono24") && (
        <FormControlLabel
          control={
            <FlatCheckbox
              checked={!!item.is_shareable}
              onChange={(e) => handleShareableChange(index, e.target.checked)}
              size="small" color="primary" disableRipple
            />
          }
          label={<Typography variant="caption">Shareable</Typography>}
          style={{ marginTop: 2, marginLeft: 0 }}
        />
      )}
      */}

      {/* PT Display: per-image toggle for storefront visibility. When 0, the image
          is excluded from item.tpl's gallery. Filtered by class_items.php
          getGalleryImages WHERE pt_display = 1.
          Brand feed ONLY — prestige_time images always display by definition;
          watch_source and chrono24 images never appear on PrestigeTime's storefront
          (they're external feeds with their own destinations), so the toggle is
          meaningless there. */}
      {feedKey === "brand" && (
        <FormControlLabel
          control={
            <FlatCheckbox
              checked={!!item.pt_display}
              onChange={(e) => handlePtDisplayChange(index, e.target.checked)}
              size="small" color="primary" disableRipple
            />
          }
          label={<Typography variant="caption">PT Display</Typography>}
          style={{ marginTop: 2, marginLeft: 0 }}
        />
      )}
    </Box>
  );

  return (
    <div
      ref={sectionRef}
      style={{
        marginBottom: 16,
        borderRadius: 6,
        outline: dragOver ? "2px dashed #1976d2" : "2px dashed transparent",
        backgroundColor: dragOver ? "rgba(25,118,210,0.04)" : "transparent",
        padding: 6,
        transition: "outline-color 0.15s, background-color 0.15s",
      }}
    >
      <Box display="flex" alignItems="center" mb={1} style={{ gap: 38 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {dragOver && (
          <Typography variant="caption" style={{ color: "#1976d2" }}>
            Drop to add
          </Typography>
        )}
        {isPicBigSlot && !dragOver && (
          <Chip
            icon={<StarIcon style={{ fontSize: 14 }} />}
            label="Main Photo"
            size="small"
            color="primary"
            variant="outlined"
            style={{ height: 20, fontSize: 11 }}
          />
        )}
      </Box>

      <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
        <div
          onClick={() => fileInputRef.current.click()}
          onMouseEnter={() => setDropzoneHover(true)}
          onMouseLeave={() => setDropzoneHover(false)}
          style={{
            width: 110,
            height: 170,
            border: `2px dashed ${dropzoneHover ? "#1976d2" : "rgba(0,0,0,0.2)"}`,
            borderRadius: 6,
            backgroundColor: dropzoneHover ? "rgba(25,118,210,0.06)" : "transparent",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "border-color 0.15s, background-color 0.15s",
            flexShrink: 0,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileInput}
            style={{ display: "none" }}
          />
          <AddPhotoAlternateIcon
            style={{
              fontSize: 32,
              color: dropzoneHover ? "#1976d2" : "rgba(0,0,0,0.18)",
              transition: "color 0.15s",
            }}
          />
        </div>

        {photos.length > 0 && (
          // flex:1 + minWidth:0 lets this shrink below its content width so
          // overflowX can kick in; otherwise the horizontal RLDD row runs off
          // the section border and off-screen when there are many images.
          <div style={{ flex: 1, minWidth: 0, overflowX: "auto", paddingBottom: 4 }}>
            {allSaved ? (
              <RLDD
                items={photos.map((p) => ({ ...p, id: p.image_id }))}
                layout="horizontal"
                onChange={handleReorder}
                itemRenderer={renderItem}
              />
            ) : (
              <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                {photos.map((p, i) => renderItem(p, i))}
              </Box>
            )}
          </div>
        )}
      </Box>

      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => { setPopoverAnchor(null); setPopoverPhoto(null); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableRestoreFocus
      >
        {popoverPhoto && (() => {
          const fileSize = popoverPhoto.file_size != null
            ? formatBytes(popoverPhoto.file_size)
            : popoverPhoto.file ? formatBytes(popoverPhoto.file.size) : "—";
          const w = popoverPhoto.width ?? popoverDims?.width;
          const h = popoverPhoto.height ?? popoverDims?.height;
          const dims = w && h ? `${w} × ${h} px` : "—";
          const uploader = popoverPhoto.uploaded_by || currentUser || "—";
          const uploadDate = popoverPhoto.created_at
            ? formatDate(popoverPhoto.created_at)
            : popoverPhoto.file ? "Just now" : "—";
          return (
            <Box style={{ padding: "10px 14px", minWidth: 180 }}>
              <Typography variant="caption" style={{ display: "block", color: "#888", marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Image Info
              </Typography>
              {[
                ["Size",       fileSize],
                ["Resolution", dims],
                ["Uploaded by", uploader],
                ["Date",       uploadDate],
              ].map(([label, value]) => (
                <Box key={label} display="flex" style={{ gap: 8, marginBottom: 3 }}>
                  <Typography variant="caption" style={{ color: "#888", minWidth: 80 }}>{label}</Typography>
                  <Typography variant="caption" style={{ fontWeight: 500 }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          );
        })()}
      </Popover>

      <Dialog
        open={Boolean(deleteDialogPhoto)}
        onClose={() => { setDeleteDialogPhoto(null); setDeleteDialogIndex(null); }}
      >
        <Box style={{ padding: "20px 24px 16px", minWidth: 320 }}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 16 }}>
            Select an Option
          </Typography>
          <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
            <Button
              variant="contained"
              style={{ backgroundColor: "#d32f2f", color: "#fff", justifyContent: "flex-start" }}
              onClick={() => {
                onDelete(deleteDialogPhoto, deleteDialogIndex, false);
                setDeleteDialogPhoto(null);
                setDeleteDialogIndex(null);
              }}
            >
              Delete Image + DB Data
            </Button>
            <Button
              variant="outlined"
              style={{ color: "#d32f2f", borderColor: "#d32f2f", justifyContent: "flex-start" }}
              onClick={() => {
                onDelete(deleteDialogPhoto, deleteDialogIndex, true);
                setDeleteDialogPhoto(null);
                setDeleteDialogIndex(null);
              }}
            >
              Delete Only DB Data
            </Button>
            <Button
              variant="outlined"
              onClick={() => { setDeleteDialogPhoto(null); setDeleteDialogIndex(null); }}
              style={{ justifyContent: "flex-start" }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth={false}
        fullScreen
        PaperProps={{
          style: {
            backgroundColor: "rgba(0,0,0,0.9)",
            transition: "transform 0.2s ease, opacity 0.2s ease",
            boxShadow: "none",
            overflow: "hidden", // 🚨 prevents scrollbars
          },
        }}
      >
        <Box
          onClick={() => setPreviewOpen(false)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100vw",
            height: "100vh",
            overflow: "hidden", // 🚨 double safety
            cursor: "zoom-out",
          }}
        >
          <img
            src={previewImage}
            alt=""
            style={{
              maxWidth: "100%",
              maxHeight: "100%", // 🚨 this is the key line
              objectFit: "contain",
            }}
          />
        </Box>
      </Dialog>
    </div>
  );
}

export const Images = ({ classes, photosByFeed, setPhotosByFeed, itemId, modelNo, brandId, seriesId, isEdit }) => {
  const currentUser = useSelector(AuthSelectors.userFullName);
  const hasPT = photosByFeed.prestige_time.length > 0;
  const mainFeed = hasPT ? "prestige_time" : "brand";

  // Functional update — always applies on top of current state, never stale
  const updateFeed = (feed, photos) => {
    setPhotosByFeed((prev) => ({ ...prev, [feed]: photos }));
  };

  const handleDelete = async (feed, photo, dbOnly = false) => {
    if (photo.image_id) {
      await authDelete(`images/${photo.image_id}${dbOnly ? "?dbOnly=true" : ""}`);
    }
    setPhotosByFeed((prev) => ({
      ...prev,
      [feed]: prev[feed].filter((p) =>
        photo.image_id ? p.image_id !== photo.image_id : p.previewUrl !== photo.previewUrl
      ),
    }));
  };

  // Upload immediately when editing an existing watch (itemId and modelNo are known).
  // Returns the saved image records from the backend.
  const buildUploadNow = (feed) => {
    if (!isEdit || !itemId || !modelNo) return null;
    return async (files, ptDisplays = []) => {
      const results = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fd = new FormData();
        fd.append("File", file);
        fd.append("0", "");
        if (feed === "brand") {
          fd.append("own_pic", "0");
          // Only the first/main brand image defaults visible; the rest upload
          // unchecked (see addFiles). Other feeds omit pt_display → backend
          // defaults to 1 (prestige_time must stay visible).
          fd.append("pt_display", String(ptDisplays[i] ?? 1));
        }
        fd.append("uploaded_by", currentUser);
        const resp = await AuthRequest.post(
          // cleanModelNo converts `/`→`~`, ` `→`_`, `.`→`-` so the URL never needs
          // %-encoding. encodeURIComponent alone wouldn't work because Apache
          // (AllowEncodedSlashes Off by default) rejects URLs with %2F → 404.
          // Backend's buildFilename re-runs cleanModelNo internally, which is
          // idempotent on the already-cleaned value.
          `/images/${cleanModelNo(modelNo)}?item=${itemId}&feed=${feed}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        if (resp.data?.images) results.push(...resp.data.images);
      }
      return results;
    };
  };

  return (
    <Accordion expanded style={{ width: "100%" }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ display: "none" }}>
        <Typography>Images</Typography>
      </AccordionSummary>
      <AccordionDetails style={{ display: "block" }}>
        {FEEDS.map(({ key, label }, i) => (
          <React.Fragment key={key}>
            {i > 0 && <Divider style={{ margin: "12px 0" }} />}
            <FeedSection
              feedKey={key}
              label={label}
              photos={photosByFeed[key]}
              isMainFeed={key === mainFeed}
              onChange={(photos) => updateFeed(key, photos)}
              onDelete={(photo, index, dbOnly) => handleDelete(key, photo, dbOnly)}
              uploadNow={buildUploadNow(key)}
              itemId={itemId}
              brandId={brandId}
              seriesId={seriesId}
              modelNo={modelNo}
              currentUser={currentUser}
            />
          </React.Fragment>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};